/**
 * Validate Sync Data - Single-page tool that reuses the Bulk Operations
 * FP selection UX (FilterCard + paginated multi-select table). Polls progress
 * via /sync/validate/{id} every 2s. WebSocket-free.
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Radio,
  RadioGroup,
} from '@mui/material'
import {
  PlayArrow as StartIcon,
} from '@mui/icons-material'

import AdminPageHeader from '../../../../components/admin/shared/AdminPageHeader'
import ValidationProgress from './components/ValidationProgress'
import ValidationResults from './components/ValidationResults'
import {
  DataScope,
  LastValidatedMap,
  ValidationRequest,
  ValidationResponse,
  ValidationStatus,
  ValidationSystem,
} from './types'
import { api, apiClient } from '../../../../services/api'
import { poolwerxColors } from '../../../../theme/colors'
import { LightspeedIcon, XeroIcon, SkimmerIcon } from '../../../../components/SystemIcons'
import {
  useFranchisePartnersData,
  FranchisePartner,
} from '../../../../components/admin/franchise-partners/hooks/useFranchisePartnersData'
import { useFranchisePartnersFilters } from '../../../../components/admin/franchise-partners/hooks/useFranchisePartnersFilters'
import { useBulkOperations } from '../../../../components/admin/franchise-partners/hooks/useBulkOperations'
import FilterCard from '../../../../components/admin/franchise-partners/filters/FilterCard'
import PartnerSelectionTable, { PartnerExtraColumn } from '../../../../components/admin/franchise-partners/PartnerSelectionTable'

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return 'Never'
  const then = new Date(iso).getTime()
  if (isNaN(then)) return 'Never'
  const diffMs = Date.now() - then
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  const diffMo = Math.round(diffDay / 30)
  return `${diffMo}mo ago`
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '-'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '-'
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const renderSystems = (fp: FranchisePartner) => {
  const cells: React.ReactNode[] = []
  if (fp.has_complete_lightspeed_connection) {
    cells.push(<LightspeedIcon key="ls" sx={{ width: 18, height: 18 }} size={18} />)
  }
  if (fp.has_complete_xero_connection) {
    cells.push(<XeroIcon key="xe" sx={{ width: 18, height: 18 }} size={18} />)
  }
  if (fp.has_complete_skimmer_connection) {
    cells.push(<SkimmerIcon key="sk" sx={{ width: 18, height: 18 }} size={18} />)
  }
  if (cells.length === 0) {
    return <Typography variant="caption" color="textSecondary">-</Typography>
  }
  return <Box sx={{ display: 'flex', gap: 0.5 }}>{cells}</Box>
}

const renderLastValidated = (lastValidated: LastValidatedMap) => (fp: FranchisePartner) => {
  const lastIso = lastValidated[fp.fp_code]
  if (!lastIso) {
    return <Typography variant="body2" color="textSecondary">Never</Typography>
  }
  return (
    <Box>
      <Typography variant="body2">{formatDate(lastIso)}</Typography>
      <Typography variant="caption" color="textSecondary">{formatRelative(lastIso)}</Typography>
    </Box>
  )
}

const validateExtraColumns = (lastValidated: LastValidatedMap): PartnerExtraColumn[] => [
  { key: 'systems', header: 'Systems', render: renderSystems },
  { key: 'last_validated', header: 'Last validated', render: renderLastValidated(lastValidated) },
]

const ValidateSyncData: React.FC = () => {
  const { franchisePartners, loading: fpsLoading } = useFranchisePartnersData()
  const {
    filters,
    updateFilter,
    clearFilters,
    filteredData,
    paginatedData,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useFranchisePartnersFilters(franchisePartners)
  const {
    selectedFranchises,
    handleSelectFranchise,
    handleSelectAll,
    handleSelectAllFiltered,
    clearSelection,
    isSelected,
    isAllSelected,
    isAllFilteredSelected,
    isIndeterminate,
  } = useBulkOperations()

  const [selectedSystems, setSelectedSystems] = useState<ValidationSystem[]>(['lightspeed', 'xero'])
  const [dataScope, setDataScope] = useState<DataScope>('all')
  const [validationId, setValidationId] = useState<string | null>(null)
  const [validationStatus, setValidationStatus] = useState<ValidationStatus | null>(null)
  const [lastValidated, setLastValidated] = useState<LastValidatedMap>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)
  const [attachedToExisting, setAttachedToExisting] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    let cancelled = false
    const loadLast = async () => {
      try {
        const map: LastValidatedMap = await api.get('/sync/validate/last-by-fp')
        if (!cancelled) setLastValidated(map || {})
      } catch (err) {
        console.warn('Failed to load last-validated map', err)
      }
    }
    loadLast()
    return () => {
      cancelled = true
    }
  }, [])

  const selectionCount = selectedFranchises.size
  const totalOps = selectionCount * selectedSystems.length

  const handleSystemChange = (system: ValidationSystem) => {
    setSelectedSystems((prev) =>
      prev.includes(system) ? prev.filter((s) => s !== system) : [...prev, system],
    )
  }

  const handleStartValidation = async () => {
    if (selectionCount === 0 || selectedSystems.length === 0) {
      setError('Please select at least one franchise partner and one system')
      return
    }
    try {
      setLoading(true)
      setError(null)
      setIsComplete(false)
      const selectedCodes = franchisePartners
        .filter((fp) => selectedFranchises.has(fp.fp_code))
        .sort((a, b) => a.franchise_name.localeCompare(b.franchise_name))
        .map((fp) => fp.fp_code)
      const request: ValidationRequest = {
        fp_codes: selectedCodes,
        systems: selectedSystems,
        data_types: dataScope === 'sales' ? ['sales'] : null,
      }
      const response: ValidationResponse = await api.post('/sync/validate', request)
      setValidationId(response.validation_id)
      if (response.was_existing) {
        setError(null)
        setAttachedToExisting(true)
      } else {
        setAttachedToExisting(false)
      }
    } catch (err: any) {
      console.error('Failed to start validation:', err)
      setError(err.response?.data?.detail || 'Failed to start validation')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setValidationId(null)
    setValidationStatus(null)
    setIsComplete(false)
    setError(null)
    setAttachedToExisting(false)
  }

  const handleCancel = async () => {
    if (!validationId) return
    try {
      setCancelling(true)
      await api.post(`/sync/validate/${validationId}/cancel`)
      // The 2s poll loop picks up the new cancelled status and stops on its own.
    } catch (err: any) {
      console.error('Cancel failed', err)
      setError(err.response?.data?.detail || 'Failed to cancel validation')
    } finally {
      setCancelling(false)
    }
  }

  const handleExport = async () => {
    if (!validationId) return
    try {
      const response = await apiClient.get(
        `/sync/validate/${validationId}/download`,
        { responseType: 'blob' },
      )
      const disposition =
        response.headers['content-disposition'] || response.headers['Content-Disposition']
      let filename: string | null = null
      if (typeof disposition === 'string') {
        const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
        if (match) filename = decodeURIComponent(match[1])
      }
      if (!filename) filename = `validation_${validationId}.xlsx`
      const url = window.URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed', err)
    }
  }

  const selectionSummary = useMemo(() => {
    if (selectionCount === 0) return 'No franchise partners selected.'
    return `${selectionCount} franchise partner${selectionCount > 1 ? 's' : ''} selected · ${totalOps} validation${totalOps !== 1 ? 's' : ''} planned`
  }, [selectionCount, totalOps])

  const isRunning = validationId !== null && !isComplete

  return (
    <>
      <AdminPageHeader
        pageKey="dataSync"
        titleOverride="Validate Sync Data"
        descriptionOverride="Compare live API counts against the database to spot sync gaps and discrepancies."
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Data Sync', to: '/admin/data-sync/active' },
          { label: 'Sync Tools', to: '/admin/data-sync/tools' },
          { label: 'Validate Sync Data' },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!validationId && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>1. Filter partners</Typography>
          <FilterCard
            filters={filters}
            onFilterChange={updateFilter}
            onClearFilters={clearFilters}
            loading={fpsLoading}
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>2. Select partners</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} results
              {selectionCount > 0 && ` | ${selectionCount} selected`}
            </Typography>
            {selectionCount > 0 && (
              <Button size="small" onClick={clearSelection}>Clear selection</Button>
            )}
          </Box>

          <PartnerSelectionTable
            paginatedData={paginatedData}
            filteredData={filteredData}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            isSelected={isSelected}
            isAllSelected={isAllSelected}
            isAllFilteredSelected={isAllFilteredSelected}
            isIndeterminate={isIndeterminate}
            handleSelectFranchise={handleSelectFranchise}
            handleSelectAll={handleSelectAll}
            handleSelectAllFiltered={handleSelectAllFiltered}
            clearSelection={clearSelection}
            loading={fpsLoading}
            extraColumns={validateExtraColumns(lastValidated)}
          />

          <Typography variant="h6" sx={{ mb: 1 }}>3. Configure & run</Typography>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Systems to validate</Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSystems.includes('lightspeed')}
                          onChange={() => handleSystemChange('lightspeed')}
                          sx={{ color: poolwerxColors.teal, '&.Mui-checked': { color: poolwerxColors.teal } }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LightspeedIcon sx={{ width: 20, height: 20 }} size={20} />
                          <span>Lightspeed</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSystems.includes('xero')}
                          onChange={() => handleSystemChange('xero')}
                          sx={{ color: poolwerxColors.teal, '&.Mui-checked': { color: poolwerxColors.teal } }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <XeroIcon sx={{ width: 20, height: 20 }} size={20} />
                          <span>Xero</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSystems.includes('skimmer')}
                          onChange={() => handleSystemChange('skimmer')}
                          sx={{ color: poolwerxColors.teal, '&.Mui-checked': { color: poolwerxColors.teal } }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SkimmerIcon sx={{ width: 20, height: 20 }} size={20} />
                          <span>Skimmer</span>
                        </Box>
                      }
                    />
                  </FormGroup>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Data scope</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                    Restrict validation to a specific entity type. Useful for fast cross-network scans.
                  </Typography>
                  <RadioGroup value={dataScope} onChange={(e) => setDataScope(e.target.value as DataScope)}>
                    <FormControlLabel
                      value="all"
                      control={<Radio sx={{ color: poolwerxColors.teal, '&.Mui-checked': { color: poolwerxColors.teal } }} />}
                      label="All data types"
                    />
                    <FormControlLabel
                      value="sales"
                      control={<Radio sx={{ color: poolwerxColors.teal, '&.Mui-checked': { color: poolwerxColors.teal } }} />}
                      label="Sales only (fast scan)"
                    />
                  </RadioGroup>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Alert
                severity={selectionCount > 0 && selectedSystems.length > 0 ? 'info' : 'warning'}
                icon={false}
                sx={{ mb: 2, py: 0.5 }}
              >
                {selectionSummary}
                {selectedSystems.length > 0 && selectionCount > 0 && (
                  <> · systems: <strong>{selectedSystems.join(', ')}</strong>
                    {dataScope === 'sales' && <> · scope: <strong>sales only</strong></>}
                  </>
                )}
              </Alert>

              <Button
                onClick={handleStartValidation}
                variant="contained"
                startIcon={<StartIcon />}
                disabled={loading || selectionCount === 0 || selectedSystems.length === 0}
                size="large"
                sx={{
                  backgroundColor: poolwerxColors.teal,
                  '&:hover': { backgroundColor: poolwerxColors.darkCyan },
                }}
              >
                {loading ? 'Starting...' : 'Start Validation'}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {validationId && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Run</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isRunning && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </Button>
              )}
              <Button variant="outlined" onClick={handleReset}>
                Start new validation
              </Button>
            </Box>
          </Box>

          {attachedToExisting && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Attached to an existing in-flight validation - watching that run instead of starting a new one.
            </Alert>
          )}

          <ValidationProgress
            validationId={validationId}
            onStatusUpdate={setValidationStatus}
            onComplete={() => setIsComplete(true)}
          />

          {validationStatus && (
            <>
              <Divider sx={{ my: 3 }} />
              <ValidationResults validationStatus={validationStatus} onExport={handleExport} />
            </>
          )}

          {isRunning && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
              Polling every 2 seconds. Safe to keep this tab open.
            </Typography>
          )}
        </>
      )}
    </>
  )
}

export default ValidateSyncData
